import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { GameConfig, AIPersonality, Faction } from '../../types/kotch';
import FactionPicker from './FactionPicker';
import { getRandomFaction } from '../../data/kotch/factionDecks';

// Game mode card images
import vsAIImage from './vsAI.jpg';
import vsLocalImage from './vsLocal.jpg';
import vsOnlineImage from './vsOnline.jpg';

type GameMode = 'menu' | 'vs-ai' | 'local' | 'online';

interface GameSetupProps {
  onStartGame: (config: GameConfig) => void;
  onStartOnline: () => void;
}

export default function GameSetup({ onStartGame, onStartOnline }: GameSetupProps) {
  const [mode, setMode] = useState<GameMode>('menu');

  const [player1Name, setPlayer1Name] = useState('Commander');
  const [player2Name, setPlayer2Name] = useState('Opponent');
  const [aiLevel, setAiLevel] = useState<AIPersonality>('medium');
  const [player1Faction, setPlayer1Faction] = useState<Faction | null>(null);
  const [player2Faction, setPlayer2Faction] = useState<Faction | null>(null);

  const handleStartVsAI = () => {
    // AI gets a random faction if player selected one, otherwise no faction
    const aiFaction = player1Faction ? getRandomFaction() : undefined;

    const config: GameConfig = {
      player1Setup: {
        name: player1Name || 'Player 1',
        isAI: false,
        faction: player1Faction || undefined,
      },
      player2Setup: {
        name: player2Name || 'AI Opponent',
        isAI: true,
        aiPersonality: aiLevel,
        faction: aiFaction,
      },
      cardsPerBattle: 10,
      battlesToWin: 2,
    };
    onStartGame(config);
  };

  const handleStartLocal = () => {
    const config: GameConfig = {
      player1Setup: {
        name: player1Name || 'Player 1',
        isAI: false,
        faction: player1Faction || undefined,
      },
      player2Setup: {
        name: player2Name || 'Player 2',
        isAI: false,
        faction: player2Faction || undefined,
      },
      cardsPerBattle: 10,
      battlesToWin: 2,
    };
    onStartGame(config);
  };

  const renderMenu = () => (
    <motion.div
      key="menu"
      className="kotch-setup-menu"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="kotch-mode-cards">
        <motion.button
          className="kotch-mode-card"
          onClick={() => setMode('vs-ai')}
          whileHover={{ y: -8, scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="kotch-mode-card-image" style={{ backgroundImage: `url(${vsAIImage})` }} />
          <div className="kotch-mode-card-overlay">
            <span className="kotch-mode-card-label">vs AI</span>
          </div>
        </motion.button>

        <motion.button
          className="kotch-mode-card"
          onClick={() => setMode('local')}
          whileHover={{ y: -8, scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="kotch-mode-card-image" style={{ backgroundImage: `url(${vsLocalImage})` }} />
          <div className="kotch-mode-card-overlay">
            <span className="kotch-mode-card-label">Local 2P</span>
          </div>
        </motion.button>

        <motion.button
          className="kotch-mode-card"
          onClick={onStartOnline}
          whileHover={{ y: -8, scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="kotch-mode-card-image" style={{ backgroundImage: `url(${vsOnlineImage})` }} />
          <div className="kotch-mode-card-overlay">
            <span className="kotch-mode-card-label">Online</span>
          </div>
        </motion.button>
      </div>
    </motion.div>
  );

  const renderVsAI = () => (
    <motion.div
      key="vs-ai"
      className="kotch-setup-form"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="kotch-setup-section">
        <h3>Your Name</h3>
        <div className="kotch-input-group">
          <input
            type="text"
            className="kotch-input"
            value={player1Name}
            onChange={(e) => setPlayer1Name(e.target.value)}
            placeholder="Enter name"
          />
        </div>
      </div>

      <div className="kotch-setup-section">
        <h3>AI Difficulty</h3>
        <div className="kotch-difficulty-buttons">
          {(['easy', 'medium', 'hard'] as AIPersonality[]).map((level) => (
            <button
              key={level}
              type="button"
              className={`kotch-button kotch-difficulty-btn ${aiLevel === level ? 'active' : ''}`}
              onClick={() => setAiLevel(level)}
            >
              {level.charAt(0).toUpperCase() + level.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="kotch-setup-section">
        <FactionPicker
          selectedFaction={player1Faction}
          onSelectFaction={setPlayer1Faction}
          label="Choose Your Faction"
        />
      </div>

      <div className="kotch-setup-actions">
        <button className="kotch-button kotch-button-secondary" onClick={() => setMode('menu')}>
          Back
        </button>
        <button className="kotch-button kotch-button-primary" onClick={handleStartVsAI}>
          Start Match
        </button>
      </div>
    </motion.div>
  );

  const renderLocal = () => (
    <motion.div
      key="local"
      className="kotch-setup-form"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="kotch-setup-section">
        <h3>Player 1</h3>
        <div className="kotch-input-group">
          <input
            type="text"
            className="kotch-input"
            value={player1Name}
            onChange={(e) => setPlayer1Name(e.target.value)}
            placeholder="Enter name"
          />
        </div>
      </div>

      <div className="kotch-setup-section">
        <h3>Player 2</h3>
        <div className="kotch-input-group">
          <input
            type="text"
            className="kotch-input"
            value={player2Name}
            onChange={(e) => setPlayer2Name(e.target.value)}
            placeholder="Enter name"
          />
        </div>
      </div>

      <div className="kotch-setup-section kotch-faction-pickers-row">
        <FactionPicker
          selectedFaction={player1Faction}
          onSelectFaction={setPlayer1Faction}
          label="Player 1 Faction"
        />
        <FactionPicker
          selectedFaction={player2Faction}
          onSelectFaction={setPlayer2Faction}
          label="Player 2 Faction"
        />
      </div>

      <div className="kotch-setup-actions">
        <button type="button" className="kotch-button kotch-button-secondary" onClick={() => setMode('menu')}>
          Back
        </button>
        <button type="button" className="kotch-button kotch-button-primary" onClick={handleStartLocal}>
          Start Match
        </button>
      </div>
    </motion.div>
  );

  return (
    <motion.div
      className="kotch-setup"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h2>New Match</h2>

      <AnimatePresence mode="wait">
        {mode === 'menu' && renderMenu()}
        {mode === 'vs-ai' && renderVsAI()}
        {mode === 'local' && renderLocal()}
      </AnimatePresence>
    </motion.div>
  );
}
